import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import DateRange from '../common/date-range'
import Pagination from '../common/pagination'
import { getCompanyItems, getCompanyNames } from '../../redux/actions/itemAction'
import { formatDay, formatMoney, getErrorMessage, unitPrice } from '../../utils'

const LIMIT = 10

const nameKey = (name = '') => name.trim().replace(/\s+/g, ' ').toLowerCase()

// Line up both vendors' items by name: one row per item, items sold by both vendors first.
// A vendor with several entries of the same item gets one row per entry, newest first.
const alignRows = (itemsA, itemsB) => {
    const groups = new Map()
    const add = (item, side) => {
        const key = nameKey(item.name)
        if (!groups.has(key)) groups.set(key, { name: item.name, a: [], b: [] })
        groups.get(key)[side].push(item)
    }
    itemsA.forEach(item => add(item, 'a'))
    itemsB.forEach(item => add(item, 'b'))

    const rank = (g) => (g.a.length && g.b.length ? 0 : g.a.length ? 1 : 2)
    return [...groups.values()]
        .sort((x, y) => rank(x) - rank(y) || x.name.localeCompare(y.name))
        .flatMap(g => Array.from({ length: Math.max(g.a.length, g.b.length) }, (_, i) => ({
            key: `${nameKey(g.name)}-${i}`, name: g.name, first: i === 0, both: rank(g) === 0, a: g.a[i], b: g.b[i],
        })))
}

// Items one vendor sold in the selected date range
const useCompanyItems = (company, from, to) => {
    const dispatch = useDispatch()
    const [state, setState] = useState({ items: [], loading: false })

    useEffect(() => {
        if (!company || (from && to && from > to)) return setState({ items: [], loading: false })
        let stale = false
        setState(prev => ({ ...prev, loading: true }))
        dispatch(getCompanyItems({ company, from: from || undefined, to: to || undefined }))
            .then(res => !stale && setState({ items: res.data.result, loading: false }))
            .catch(err => {
                if (stale) return
                setState({ items: [], loading: false })
                toast.error(getErrorMessage(err))
            })
        return () => { stale = true }
    }, [dispatch, company, from, to])

    return state
}

// Date, quantity, price and price per quantity of one vendor's entry
const VendorCells = ({ item, other }) => {
    if (!item) return <><td className="VendorStart">-</td><td>-</td><td>-</td><td>-</td></>
    const unit = unitPrice(item)
    const otherUnit = unitPrice(other)
    const verdict = !unit || !otherUnit || unit === otherUnit ? '' : unit < otherUnit ? 'Cheaper' : 'Costlier'
    return (
        <>
            <td className="VendorStart text-nowrap">{formatDay(item.effectiveDate)}</td>
            <td>{item.quantity}</td>
            <td className="text-nowrap">{formatMoney(item.price)}</td>
            <td className={`text-nowrap ${verdict}`}>{unit == null ? '-' : formatMoney(unit)}</td>
        </>
    )
}

// Which vendor is cheaper per quantity on this row, and by how much
const BestDeal = ({ row }) => {
    const a = unitPrice(row.a)
    const b = unitPrice(row.b)
    if (!a || !b) return <td className="VendorStart">-</td>
    if (a === b) return <td className="VendorStart"><span className="DealBadge DealSame">Same price</span></td>
    const side = a < b ? 'A' : 'B'
    const saving = Math.round((Math.abs(a - b) / Math.max(a, b)) * 100)
    return (
        <td className="VendorStart text-nowrap">
            <span className={`DealBadge Deal${side}`}><i className="fa fa-trophy"></i> Vendor {side}</span>
            {saving > 0 && <small className="DealSaving">{saving}% cheaper</small>}
        </td>
    )
}

const VendorCompare = () => {
    const dispatch = useDispatch()
    const [companies, setCompanies] = useState([])
    const [vendorA, setVendorA] = useState('')
    const [vendorB, setVendorB] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')

    useEffect(() => {
        dispatch(getCompanyNames()).then(res => setCompanies(res.data)).catch(err => toast.error(getErrorMessage(err)))
    }, [dispatch])

    const a = useCompanyItems(vendorA, from, to)
    const b = useCompanyItems(vendorB, from, to)
    const loading = a.loading || b.loading
    const rows = alignRows(a.items, b.items)

    // Client-side paging: both vendors' lists are already loaded in full
    const [page, setPage] = useState(1)
    useEffect(() => { setPage(1) }, [vendorA, vendorB, from, to])
    const pages = Math.ceil(rows.length / LIMIT) || 1
    const pageRows = rows.slice((page - 1) * LIMIT, page * LIMIT)

    const vendorSelect = (label, value, onChange, other) => (
        <div className="form-group">
            <label>{label}</label>
            <select className="form-control" value={value} onChange={e => onChange(e.target.value)}>
                <option value="">— Select company —</option>
                {companies.filter(c => c.name !== other).map(c => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
        </div>
    )

    return (
        <div className="Small-Wrapper">
            <div className="TitleBox">
                <h2>Vendor Price Comparison</h2>
            </div>

            <div className="Filter">
                {vendorSelect('Vendor A', vendorA, setVendorA, vendorB)}
                {vendorSelect('Vendor B', vendorB, setVendorB, vendorA)}
                <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t) }} />
                <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="Button Cancel" title="Reset filters" onClick={() => { setVendorA(''); setVendorB(''); setFrom(''); setTo('') }}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>
            </div>

            <div className="TableList VendorTable">
                <table>
                    <thead>
                        <tr>
                            <th rowSpan="2">Item Name</th>
                            <th colSpan="4" className="VendorStart">Vendor A{vendorA ? ` — ${vendorA}` : ''}</th>
                            <th colSpan="4" className="VendorStart">Vendor B{vendorB ? ` — ${vendorB}` : ''}</th>
                            <th rowSpan="2" className="VendorStart">Best Deal</th>
                        </tr>
                        <tr>
                            {['a', 'b'].map(side => (
                                <React.Fragment key={side}>
                                    <th className="VendorStart">Date</th>
                                    <th>Quantity</th>
                                    <th>Price</th>
                                    <th>Price / Qty</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map(row => (
                            <tr key={row.key} className={row.both ? 'BothRow' : ''}>
                                <td className="text-left">{row.first || row === pageRows[0] ? row.name : ''}</td>
                                <VendorCells item={row.a} other={row.b} />
                                <VendorCells item={row.b} other={row.a} />
                                <BestDeal row={row} />
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan="10">{!vendorA && !vendorB ? 'Select Vendor A and Vendor B above to compare their prices.' : loading ? 'Loading...' : 'No items in the selected date range.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <p className="VendorLegend">Price / Qty: <span className="Cheaper">green</span> = cheaper than the other vendor on the same row, <span className="Costlier">red</span> = costlier</p>

            <div className="ListFooter">
                <span>Total: {rows.length} · Items sold by both vendors: {rows.filter(r => r.both && r.first).length}</span>
                <Pagination page={Math.min(page, pages)} pages={pages} onChange={setPage} />
            </div>
        </div>
    )
}

export default VendorCompare
